import {FC, useState, useEffect} from "react";
import {useFormik} from "formik";
import * as Yup from "yup";
import {useNavigate, useLocation, useSearchParams} from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Avatar,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {useSendOtpMutation} from "../../../app/services/otp";
import {getApiErrorMessage} from "../../../util/getApiErrorMessage";
import {CustomerPortalConfig} from "../../../app/interfaces/dealerCustomer";
import {ALLOWED_PARENT_ORIGINS, withHostOrigin} from "../../../util/helpers";
import {
  getCustomerPortalIdentifier,
  saveCustomerPortalIdentifier,
} from "../../../util/customerPortalIdentifier";

const isEmail = (value: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
};

export const OtpRequestPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [sendOtp, {isLoading}] = useSendOtpMutation();

  const [hostOrigin, setHostOrigin] = useState<string | null>(null);
  // Inline status message shown inside the iframe instead of a toast.
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const [config, setConfig] = useState<CustomerPortalConfig | null>(
    location.state?.config || null
  );

  const dealerWebsite = config?.dealerWebsite || "";

  // Only trust a hostOrigin that is in the configured allowlist.
  useEffect(() => {
    const hostOriginParam = searchParams.get("hostOrigin");
    if (hostOriginParam && ALLOWED_PARENT_ORIGINS.includes(hostOriginParam)) {
      setHostOrigin(hostOriginParam);
    } else if (hostOriginParam) {
      console.error("Untrusted hostOrigin:", hostOriginParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (config) return;

    const handleMessage = (event: MessageEvent) => {
      // Only accept config from an allowlisted parent origin. The dealer website is taken from the
      // payload (trusted per product decision), not derived from event.origin.
      if (!ALLOWED_PARENT_ORIGINS.includes(event.origin)) {
        console.debug("Ignored config message from untrusted origin:", event.origin);
        return;
      }

      if (event.data?.type === "CUSTOMER_PORTAL_CONFIG") {
        setConfig({
          productId: event.data.productId,
          dealerWebsite: event.data.dealerWebsite,
          clientId: event.data.clientId,
          clientSecret: event.data.clientSecret,
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [hostOrigin, config]);

  useEffect(() => {
    if (hostOrigin && !config) {
      window.parent.postMessage({type: "REQUEST_CUSTOMER_PORTAL_CONFIG"}, hostOrigin);
    }
  }, [hostOrigin, config]);

  // Validation schema
  const validationSchema = Yup.object({
    identifier: Yup.string()
      .required("Email or Phone Number is required")
      .test(
        "email-or-phone",
        "Please enter a valid email or phone number",
        value => {
          if (!value) return false;
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const phoneRegex = /^[\d\s()+-]+$/;
          return emailRegex.test(value) || phoneRegex.test(value);
        },
      ),
  });

  const formik = useFormik({
    // Prefill with the identifier the customer used last time (from local storage).
    initialValues: {
      identifier: getCustomerPortalIdentifier(),
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      setStatusMessage(null);

      // Config (incl. productId) arrives from the host via postMessage; block submit until it's
      // here so we surface a clear message instead of a server-side "ProductId is required".
      if (!config?.productId) {
        setStatusMessage("The portal is still loading. Please try again in a moment.");
        return;
      }

      try {
        const identifier = values.identifier.trim();
        const isEmailInput = isEmail(identifier);

        const response = await sendOtp({
          email: isEmailInput ? identifier : "",
          phoneNumber: isEmailInput ? undefined : identifier,
          dealerWebsite: dealerWebsite,
          productId: config.productId,
        }).unwrap();

        // Remember the identifier for next time.
        saveCustomerPortalIdentifier(identifier);

        navigate(withHostOrigin("/otp-verification", hostOrigin), {
          state: {
            sessionId: response.sessionId,
            identifier: identifier,
            maskedEmail: response.maskedEmail,
            maskedPhoneNumber: response.maskedPhoneNumber,
            channel: response.channel,
            dealerWebsite: dealerWebsite,
            config: config,
            notice: response.message || "A one-time passcode has been sent.",
          },
        });
      } catch (error) {
        const errorMessage = getApiErrorMessage(error);
        setStatusMessage(errorMessage || "Failed to send OTP. Please try again.");
      }
    },
  });

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "background.paper",
          padding: 4,
          borderRadius: 2,
          boxShadow: 3,
          width: "100%",
        }}>
        <Avatar sx={{bgcolor: "primary.main", mb: 2}}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography component="h1" variant="h5" sx={{mb: 1}}>
          Customer Portal Authentication
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{mb: 3}}>
          Enter your email or phone number to receive a One Time Passcode
        </Typography>

        {statusMessage && (
          <Alert
            severity="error"
            onClose={() => setStatusMessage(null)}
            sx={{width: "100%", mb: 2}}>
            {statusMessage}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit} style={{width: "100%"}}>
          <TextField
            size="small"
            variant="outlined"
            margin="normal"
            fullWidth
            label="Email or Phone Number"
            name="identifier"
            placeholder="Enter your Email or Phone Number"
            autoFocus
            value={formik.values.identifier}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.identifier && Boolean(formik.errors.identifier)}
            helperText={formik.touched.identifier && formik.errors.identifier}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{mt: 3, mb: 2, py: 1.2}}
            disabled={isLoading}>
            {isLoading ? "Sending..." : "Send Passcode"}
          </Button>
        </form>
      </Box>
    </Container>
  );
};
